import { useRef, useState } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';

import { useModal } from '~/features/modals/useModal';
import { PatientCreateSteps } from '~/features/patientCreation/constants';
import {
  type PatientCreationFormState,
  type PatientInformationRef,
  type PatientIntroFormRef,
  type PatientSettingsFormRef,
} from '~/features/patientCreation/types';
import { usePatientCreationForm } from '~/features/patientCreation/usePatientCreationForm';
import { buildTestId } from '~/utils/buildTestId';

import { FoundPatientMessage } from './FoundPatientMessage';
import { ContentLoader } from '../ContentLoader';
import { PatientInformationForm } from '../forms/PatientInformationForm';
import { PatientIntroForm } from '../forms/PatientIntroForm';
import { PatientSettingsForm } from '../forms/patientSettings/PatientSettingsForm';
import { Modal } from '../Modal';
import { PatientFoundTable } from '../table/patientCreate/PatientFoundTable';

const getModalTitle = (currentStep: PatientCreationFormState['step']) => {
  switch (currentStep) {
    case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
      return 'Patient Already Exists';
    case PatientCreateSteps.PATIENT_SETTINGS:
      return 'Patient App Data Display and Alert Settings';
    case PatientCreateSteps.PATIENT_EMAIL_ACCOUNT_WAS_FOUND:
    case PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND:
      return 'A Patient Account Was Found';
    case PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND:
      return 'Multiple Patient Accounts Were Found';
    default:
      return 'Add Patient';
  }
};

const TEST_ID = 'patient-create' as const;

export const PatientCreateModal = () => {
  const introFormRef = useRef<PatientIntroFormRef>(null);
  const settingsFormRef = useRef<PatientSettingsFormRef>(null);
  const informationFormRef = useRef<PatientInformationRef>(null);
  const [exitConfirmation, setExitConfirmation] = useState(false);
  const { openModal, closeModal } = useModal();

  const {
    currentStep,
    onModalClose,
    goToNextStep,
    isNextStepDisabled,
    goToPreviousStep,
    isFormLoading,
    onPatientIntroSubmit,
    setIsNextStepDisabled,
    onPatientInformationSubmit,
    values,
    foundPatients,
    selectedFoundPatientId,
    selectFoundPatient,
    resetForm,
  } = usePatientCreationForm({
    introFormRef,
    settingsFormRef,
    informationFormRef,
  });

  const handleOpenChange = (open: boolean) => {
    if (!exitConfirmation) {
      openModal('ExitIntent', {
        content:
          'You haven’t finished adding this patient. If you leave now, you will lose your progress.',
        onExitConfirmation: () => {
          setExitConfirmation(true);
          resetForm();
          closeModal('PatientCreate');
        },
      });

      return false;
    }

    if (!open) {
      onModalClose();
    }

    return true;
  };

  return (
    <Modal
      testID={TEST_ID}
      title={getModalTitle(currentStep)}
      hasCloseButton
      size={
        currentStep === PatientCreateSteps.PATIENT_EMAIL_ACCOUNT_WAS_FOUND ||
        currentStep === PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND ||
        currentStep === PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND
          ? 'l'
          : 'm'
      }
      primaryAction={{
        label:
          currentStep === PatientCreateSteps.PATIENT_SETTINGS
            ? 'Complete'
            : 'Next',
        onPress: () => void goToNextStep(),
        disabled: isNextStepDisabled,
      }}
      secondaryAction={{
        label:
          currentStep === PatientCreateSteps.PATIENT_INTRO ? 'Cancel' : 'Back',
        onPress: goToPreviousStep,
      }}
      onOpenChange={handleOpenChange}
    >
      {isFormLoading && (
        <ContentLoader
          testID={buildTestId({ testID: TEST_ID, suffix: 'loader' })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_INTRO && (
        <PatientIntroForm
          ref={introFormRef}
          onSubmit={onPatientIntroSubmit}
          setIsNextDisabled={setIsNextStepDisabled}
          testID={buildTestId({ testID: TEST_ID, suffix: ['intro', 'form'] })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_INFORMATION && (
        <PatientInformationForm
          ref={informationFormRef}
          defaultValues={values}
          onSubmit={onPatientInformationSubmit}
          setIsNextDisabled={setIsNextStepDisabled}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['information', 'form'],
          })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_SETTINGS && (
        <PatientSettingsForm
          ref={settingsFormRef}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['settings', 'form'],
          })}
        />
      )}

      {(currentStep === PatientCreateSteps.PATIENT_EMAIL_ACCOUNT_WAS_FOUND ||
        currentStep === PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND ||
        currentStep === PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND) &&
        foundPatients && (
          <Stack gap="$4" marginTop="$6">
            <FoundPatientMessage
              step={currentStep}
              patient={foundPatients[0]}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['found', 'message'],
              })}
            />

            <PatientFoundTable
              selectedPatientId={selectedFoundPatientId}
              foundPatients={foundPatients}
              selectFoundPatient={selectFoundPatient}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['found', 'table'],
              })}
            />
          </Stack>
        )}

      {currentStep === PatientCreateSteps.PATIENT_ALREADY_EXISTS && (
        <Text
          type="$bodyBase.default"
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['already', 'exists', 'message'],
          })}
        >
          A patient profile already exists for{' '}
          <Text type="$bodyBase.medium">
            {values.firstName} {values.lastName}
          </Text>
          .
          <br />
          Confirm or update the patient’s details on the next page.
        </Text>
      )}
    </Modal>
  );
};
